import React, { useEffect, useState } from "react";
import { Target, TrendingUp, Users, Wallet, Loader2, Edit3, Play, Send, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtEur = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Math.round(n)) + " €";
};
const fmtNum = (n, dec = 0) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("it-IT", { maximumFractionDigits: dec, minimumFractionDigits: dec }).format(n);
};

export default function StrategyCoach() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/agent/economic-plan")
      .then((r) => setPlan(r.data && Object.keys(r.data).length ? r.data : null))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-400">Caricamento…</div>;

  return (
    <div className="space-y-8 fade-slide max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-[#0A0A0A] flex items-center gap-2">
          <Target size={26} className="text-[#B40101]" /> Strategy Coach
        </h1>
        <p className="text-gray-500 mt-1">Pianifica il tuo anno e ricevi coaching personale ancorato ai tuoi dati reali.</p>
      </div>

      <section>
        <h2 className="text-lg font-bold text-[#0A0A0A] mb-3">Livello 1 — Il tuo piano economico</h2>
        {(!plan || editing) ? (
          <PlanForm
            initial={plan}
            onSaved={(p) => { setPlan(p); setEditing(false); }}
            onCancel={plan ? () => setEditing(false) : null}
          />
        ) : (
          <PlanDisplay plan={plan} onEdit={() => setEditing(true)} />
        )}
      </section>

      {plan && !editing && (
        <>
          <div className="h-px bg-gray-200 my-6" />
          <CoachSection />
        </>
      )}
    </div>
  );
}

function PlanForm({ initial, onSaved, onCancel }) {
  const [obiettivo, setObiettivo] = useState(initial?.obiettivo_reddito ?? "");
  const [comm, setComm] = useState(initial?.commissione ?? 6);
  const [quota, setQuota] = useState(initial?.quota_agente ?? 50);
  const [prezzo, setPrezzo] = useState(initial?.prezzo_medio ?? "");
  const [spese, setSpese] = useState(initial?.spese_mensili ?? "");
  const [saving, setSaving] = useState(false);

  // Strip thousands separators (. or ,) and parse as integer.
  // "30.000" -> 30000, "300,000" -> 300000, "60000" -> 60000.
  const parseInt0 = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const cleaned = String(v).replace(/[.,\s]/g, "");
    const n = parseInt(cleaned, 10);
    return Number.isFinite(n) ? n : 0;
  };
  // Percentage: accept both . and , as decimal, default to 0.
  const parsePct = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const cleaned = String(v).replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const save = async () => {
    const ob = parseInt0(obiettivo);
    const pr = parseInt0(prezzo);
    const sp = parseInt0(spese);
    const co = parsePct(comm);
    const qa = parsePct(quota);
    if (!ob || !co || !qa || !pr) { toast.error("Compila obiettivo, commissione, quota agente e prezzo medio"); return; }
    setSaving(true);
    try {
      const r = await api.put("/agent/economic-plan", {
        obiettivo_reddito: ob,
        commissione: co,
        quota_agente: qa,
        prezzo_medio: pr,
        spese_mensili: sp,
      });
      toast.success("Piano salvato");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vg-card p-6 space-y-4">
      <PlanField label="Obiettivo di reddito annuale (€)">
        <input className="vg-input" type="text" inputMode="numeric" placeholder="60000 (anche 60.000 o 60,000)" value={obiettivo} onChange={(e) => setObiettivo(e.target.value)} data-testid="plan-obiettivo-input" />
      </PlanField>
      <PlanField label="Percentuale commissione media (%)">
        <input className="vg-input" type="text" inputMode="decimal" placeholder="6" value={comm} onChange={(e) => setComm(e.target.value)} data-testid="plan-commissione-input" />
      </PlanField>
      <PlanField label="La tua quota della commissione (%)">
        <input className="vg-input" type="text" inputMode="decimal" placeholder="50" value={quota} onChange={(e) => setQuota(e.target.value)} data-testid="plan-quota-input" />
        <p className="text-xs text-gray-400 mt-1">La percentuale che ricevi tu sulla commissione totale. In agenzia tradizionale è tipicamente 50-70%. Con Keller Williams parte dal 64%.</p>
      </PlanField>
      <PlanField label="Prezzo medio immobili nella tua zona (€)">
        <input className="vg-input" type="text" inputMode="numeric" placeholder="300000 (anche 300.000 o 300,000)" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} data-testid="plan-prezzo-input" />
      </PlanField>
      <PlanField label="Spese mensili previste (€)">
        <input className="vg-input" type="text" inputMode="numeric" placeholder="800" value={spese} onChange={(e) => setSpese(e.target.value)} data-testid="plan-spese-input" />
      </PlanField>
      <div className="flex gap-2 pt-2">
        <button className="vg-btn-primary px-6 inline-flex items-center gap-2" onClick={save} disabled={saving} data-testid="plan-save-btn">
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Calcola il mio piano"}
        </button>
        {onCancel && (
          <button className="vg-btn-secondary px-6" onClick={onCancel} data-testid="plan-cancel-btn">Annulla</button>
        )}
      </div>
    </div>
  );
}

function PlanField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#0A0A0A] mb-1">{label}</span>
      {children}
    </label>
  );
}

function PlanDisplay({ plan, onEdit }) {
  const cards = [
    { icon: <TrendingUp size={18} />, label: "Volume di vendite necessario", value: fmtEur(plan.volume_vendite) },
    { icon: <Target size={18} />, label: "Transazioni necessarie", value: fmtNum(plan.transazioni) },
    { icon: <Users size={18} />, label: "Lead necessari all'anno", value: fmtNum(plan.lead_annuali) },
    { icon: <Users size={18} />, label: "Lead necessari al mese", value: fmtNum(plan.lead_mese, 2) },
    { icon: <Users size={18} />, label: "Lead necessari alla settimana", value: fmtNum(plan.lead_settimana, 2) },
    { icon: <Wallet size={18} />, label: "Spese annuali totali", value: fmtEur(plan.spese_annuali) },
    { icon: <Wallet size={18} />, label: "Margine netto stimato", value: fmtEur(plan.margine_netto), highlight: true },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            className={`vg-card p-4 ${c.highlight ? "border-l-4 border-[#B40101]" : ""}`}
            data-testid={`plan-card-${c.label.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
          >
            <div className="flex items-center gap-2 text-[#B40101] mb-1">{c.icon}<span className="text-xs font-semibold uppercase tracking-wide">{c.label}</span></div>
            <div className="text-2xl font-bold text-[#0A0A0A]">{c.value}</div>
          </div>
        ))}
      </div>
      <button className="vg-btn-secondary inline-flex items-center gap-2 px-4 py-2" onClick={onEdit} data-testid="plan-edit-btn">
        <Edit3 size={14} /> Modifica piano
      </button>
    </div>
  );
}

// ===== Coach Session =====
const PHASES = ["apertura", "domanda_2", "domanda_3", "analisi_finale"];

function CoachSection() {
  const [history, setHistory] = useState([]); // {role:'coach'|'user', text}
  const [phaseIdx, setPhaseIdx] = useState(-1); // -1 = not started
  const [pendingReply, setPendingReply] = useState("");
  const [loading, setLoading] = useState(false);

  const sendPhase = async (fase, risposte) => {
    setLoading(true);
    try {
      const r = await api.post("/agent/strategy-coach", { fase, risposte_precedenti: risposte });
      return r.data.message;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore coaching");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const start = async () => {
    setHistory([]);
    setPhaseIdx(0);
    const msg = await sendPhase("apertura", []);
    if (msg) {
      setHistory([{ role: "coach", text: msg }]);
    } else {
      setPhaseIdx(-1);
    }
  };

  const submitReply = async () => {
    if (!pendingReply.trim()) return;
    const userMsg = pendingReply.trim();
    setPendingReply("");
    const nextIdx = phaseIdx + 1;
    const nextPhase = PHASES[nextIdx];
    const updatedHistory = [...history, { role: "user", text: userMsg }];
    setHistory(updatedHistory);
    const allUserReplies = updatedHistory.filter((m) => m.role === "user").map((m) => m.text);
    const msg = await sendPhase(nextPhase, allUserReplies);
    if (msg) {
      setHistory([...updatedHistory, { role: "coach", text: msg }]);
      setPhaseIdx(nextIdx);
    }
  };

  const isFinished = phaseIdx >= PHASES.length - 1;
  const canSendReply = phaseIdx >= 0 && phaseIdx < PHASES.length - 1 && !loading;

  return (
    <section data-testid="coach-section">
      <h2 className="text-lg font-bold text-[#0A0A0A] mb-3">Livello 2 — Sessione con il tuo Coach</h2>
      {phaseIdx === -1 ? (
        <div className="vg-card p-6">
          <p className="text-sm text-gray-500 mb-4">
            Il Coach ti pone tre domande calibrate sui tuoi dati e poi chiude con un'analisi e tre azioni concrete per la settimana.
          </p>
          <button className="vg-btn-primary inline-flex items-center gap-2 px-6" onClick={start} disabled={loading} data-testid="coach-start-btn">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <><Play size={16} /> Inizia sessione</>}
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="coach-chat">
          {history.map((m, i) => (
            <div
              key={i}
              className={`p-4 rounded-[12px] ${m.role === "coach" ? "bg-[#FFF7F7] border-l-4 border-[#B40101]" : "bg-gray-100"}`}
              data-testid={`coach-msg-${m.role}-${i}`}
            >
              <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: m.role === "coach" ? "#B40101" : "#404040" }}>
                {m.role === "coach" ? "Coach" : "Tu"}
              </div>
              <div className="text-sm text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="animate-spin" size={14} /> Il Coach sta riflettendo…
            </div>
          )}
          {!isFinished && canSendReply && (
            <div className="vg-card p-4 space-y-2" data-testid="coach-reply-form">
              <textarea
                className="vg-input min-h-[90px] resize-y leading-relaxed"
                placeholder="Scrivi la tua risposta…"
                value={pendingReply}
                onChange={(e) => setPendingReply(e.target.value)}
                data-testid="coach-reply-input"
              />
              <button className="vg-btn-primary inline-flex items-center gap-2 px-6" onClick={submitReply} disabled={!pendingReply.trim() || loading} data-testid="coach-reply-send">
                <Send size={14} /> Invia risposta
              </button>
            </div>
          )}
          {isFinished && (
            <button className="vg-btn-secondary inline-flex items-center gap-2 px-6" onClick={start} disabled={loading} data-testid="coach-restart-btn">
              <ChevronRight size={14} /> Nuova sessione
            </button>
          )}
        </div>
      )}
    </section>
  );
}
